import ApiService from "./ApiService"
import moment from "moment"
import {
    GetAdvancedFilteringFiltersRequest,
    PostAdvancedFilteringRecipientsRequest,
    PostAdvancedFilterBatchRequest,
    GetAdvancedFilterBatchRecipientsRequest,
} from "../api/requestObjects"

/**
 * d6+ Advanced Filtering API Service
 * 
 * @description Handles API calls for the d6+ Advanced Filtering feature
 * which allows filtering recipients (Parents, Learners, Staff) based on
 * various criteria like grades, gender, parent type, etc.
 * 
 * Entity Types:
 * - 'parents': Filter parents based on their children's attributes
 * - 'learners': Filter learners directly
 * - 'staff': Filter staff members
 * 
 * API Endpoints (separate per entity type):
 * - GET /portal/v1/advanced-filtering/parents/filters
 * - GET /portal/v1/advanced-filtering/learners/filters
 * - GET /portal/v1/advanced-filtering/staff/filters
 * - GET /portal/v1/advanced-filtering/{entity_type}/filter-options
 * - POST /portal/v1/advanced-filtering/parents/recipients
 * - POST /portal/v1/advanced-filtering/learners/recipients
 * - POST /portal/v1/advanced-filtering/staff/recipients
 */
class AdvancedFilteringApiService extends ApiService {

    /**
     * Get the filter options endpoint for a specific entity type
     * @private
     */
    _getFilterOptionsEndpoint = (entityType) => {
        const { 
            GET_ADVANCED_FILTERING_PARENTS_FILTERS,
            GET_ADVANCED_FILTERING_LEARNERS_FILTERS,
            GET_ADVANCED_FILTERING_STAFF_FILTERS,
        } = this.endpoints

        switch (entityType) {
            case 'parents':
                return GET_ADVANCED_FILTERING_PARENTS_FILTERS
            case 'learners':
                return GET_ADVANCED_FILTERING_LEARNERS_FILTERS
            case 'staff':
                return GET_ADVANCED_FILTERING_STAFF_FILTERS
            default:
                return GET_ADVANCED_FILTERING_PARENTS_FILTERS
        }
    }

    /**
     * Get the recipients endpoint for a specific entity type
     * @private
     */
    _getRecipientsEndpoint = (entityType) => {
        const { 
            POST_ADVANCED_FILTERING_PARENTS_RECIPIENTS,
            POST_ADVANCED_FILTERING_LEARNERS_RECIPIENTS,
            POST_ADVANCED_FILTERING_STAFF_RECIPIENTS,
        } = this.endpoints

        switch (entityType) {
            case 'parents':
                return POST_ADVANCED_FILTERING_PARENTS_RECIPIENTS
            case 'learners':
                return POST_ADVANCED_FILTERING_LEARNERS_RECIPIENTS
            case 'staff':
                return POST_ADVANCED_FILTERING_STAFF_RECIPIENTS
            default:
                return POST_ADVANCED_FILTERING_PARENTS_RECIPIENTS
        }
    }

    /**
     * Fetch filter options for advanced filtering
     * Returns available status options, for options, gender options, etc.
     * 
     * @param {Object} params
     * @param {string} params.communityId - Community ID
     * @param {string} params.entityType - 'parents' | 'learners' | 'staff'
     * @returns {Promise}
     */
    getFilterOptions = (params) => {
        const { entityType, communityId } = params
        const endpoint = this._getFilterOptionsEndpoint(entityType)
        const payload = new GetAdvancedFilteringFiltersRequest({ communityId })

        return this.API(this.getEndpoint({
            path: endpoint,
            params: payload.getPayload()
        }))
    }

    /**
     * Fetch filter selection options when a for_option with metadata is selected
     * 
     * @param {Object} params
     * @param {string} params.communityId - Community ID
     * @param {string} params.entityType - 'parents' | 'learners' | 'staff'
     * @param {string} params.forValue - The selected for_options value
     * @returns {Promise}
     */
    getFilterSelectionOptions = (params) => {
        const { GET_ADVANCED_FILTERING_FILTER_OPTIONS } = this.endpoints
        const { entityType, communityId, forValue } = params

        return this.API(this.getEndpoint({
            path: GET_ADVANCED_FILTERING_FILTER_OPTIONS,
            data: entityType,
            params: { community_id: communityId, for: forValue }
        }))
    }

    /**
     * Fetch filtered recipients based on selected criteria
     * Uses legacy d6+ parameter names: status, current_on, wait_year, for, filter, which, gender
     * 
     * @param {Object} params
     * @param {string} params.communityId - Community ID
     * @param {string} params.entityType - 'parents' | 'learners' | 'staff'
     * @param {string} params.status - '1' (current), '2' (left), '7' (waitlist)
     * @param {string} params.currentOn - Date for historical lookup (YYYY-MM-DD)
     * @param {string} params.waitYear - Waitlist year
     * @param {string} params.for - Filter by option value
     * @param {Array} params.filter - Array of selected filter IDs
     * @param {string} params.which - Parent type (parents only)
     * @param {string} params.gender - Gender filter
     * @param {string} params.search - Search query
     * @param {number} params.page - Page number
     * @param {number} params.perPage - Items per page
     * @returns {Promise}
     */
    getFilteredRecipients = (params) => {
        const { entityType } = params
        const endpoint = this._getRecipientsEndpoint(entityType)
        const payload = new PostAdvancedFilteringRecipientsRequest(params)

        return this.API.post(this.getEndpoint({
            path: endpoint,
        }), payload.getPayload())
    }

    // ============================================
    // BATCH API METHODS
    // ============================================
    // These methods handle the batch-based recipient storage system
    // that links filter selections to notices for persistence.

    /**
     * Create or replace a batch for an entity type
     * If notice_id is provided and a batch already exists for that notice + entity type,
     * the old batch is replaced.
     * 
     * POST /communities/{communityId}/notices/advanced-filter-recipients/batch
     * 
     * @param {Object} params
     * @param {string} params.communityId - Community ID
     * @param {string} params.entityType - 'parents' | 'learners' | 'staff'
     * @param {Object} params.filterCriteria - The full filter state (status, for, filter, which, gender, etc.)
     * @param {string} [params.noticeId] - Optional UUID to link batch to existing draft
     * @returns {Promise} Response with batch data including id, total_recipients, status
     */
    createBatch = (params) => {
        const { POST_ADVANCED_FILTER_BATCH } = this.endpoints
        const { communityId, entityType, filterCriteria, noticeId } = params
        const payload = new PostAdvancedFilterBatchRequest({ entityType, filterCriteria, noticeId })

        return this.API.post(this.getEndpoint({
            path: POST_ADVANCED_FILTER_BATCH,
            data: communityId,
        }), payload.getPayload())
    }

    /**
     * Get batch details including filter criteria and recipient count
     * 
     * GET /communities/{communityId}/notices/advanced-filter-recipients/batch/{batchId}
     * 
     * @param {Object} params
     * @param {string} params.communityId - Community ID
     * @param {string} params.batchId - Batch UUID
     * @returns {Promise} Response with batch data including filter_criteria, total_recipients
     */
    getBatch = (params) => {
        const { GET_ADVANCED_FILTER_BATCH } = this.endpoints
        const { communityId, batchId } = params

        return this.API(this.getEndpoint({
            path: GET_ADVANCED_FILTER_BATCH,
            data: [communityId, batchId],
        }))
    }

    /**
     * Get paginated recipients from a batch
     * 
     * GET /communities/{communityId}/notices/advanced-filter-recipients/batch/{batchId}/recipients
     * 
     * @param {Object} params
     * @param {string} params.communityId - Community ID
     * @param {string} params.batchId - Batch UUID
     * @param {number} [params.page=1] - Page number
     * @param {number} [params.perPage=25] - Items per page (max 100)
     * @param {string} [params.sort] - Sort field(s), e.g. 'first_name,-last_name'
     * @param {string} [params.search] - Search query
     * @returns {Promise} Response with paginated recipients
     */
    getBatchRecipients = (params) => {
        const { GET_ADVANCED_FILTER_BATCH_RECIPIENTS } = this.endpoints
        const { communityId, batchId, page, perPage, sort, search } = params
        const payload = new GetAdvancedFilterBatchRecipientsRequest({ page, perPage, sort, search })

        return this.API(this.getEndpoint({
            path: GET_ADVANCED_FILTER_BATCH_RECIPIENTS,
            data: [communityId, batchId],
            params: payload.getPayload(),
        }))
    }

    /**
     * Delete a batch
     * 
     * DELETE /communities/{communityId}/notices/advanced-filter-recipients/batch/{batchId}
     * 
     * @param {Object} params
     * @param {string} params.communityId - Community ID
     * @param {string} params.batchId - Batch UUID
     * @returns {Promise}
     */
    deleteBatch = (params) => {
        const { DELETE_ADVANCED_FILTER_BATCH } = this.endpoints
        const { communityId, batchId } = params

        return this.API.delete(this.getEndpoint({
            path: DELETE_ADVANCED_FILTER_BATCH,
            data: [communityId, batchId],
        }))
    }

    /**
     * Get all batches linked to a notice
     * Used when loading a draft for editing to reconstruct the filter UI
     * 
     * GET /communities/{communityId}/notices/{noticeId}/advanced-filter-recipients/batches
     * 
     * @param {Object} params
     * @param {string} params.communityId - Community ID
     * @param {string} params.noticeId - Notice UUID
     * @returns {Promise} Response with array of batches for the notice
     */
    getNoticeBatches = (params) => {
        const { GET_NOTICE_ADVANCED_FILTER_BATCHES } = this.endpoints
        const { communityId, noticeId } = params

        return this.API(this.getEndpoint({
            path: GET_NOTICE_ADVANCED_FILTER_BATCHES,
            data: [communityId, noticeId],
        }))
    }

    /**
     * Helper method to convert frontend filter state to API filter_criteria format
     * 
     * Aligns with d6+ Advanced Filtering API Handler:
     * @see https://d6group.atlassian.net/wiki/spaces/d6/pages/434733059/d6+Advanced+Filtering+API+Handler
     * 
     * Learner Status Values (d6+ API):
     *   1 = Current learners (default)
     *   2 = Admitted / future learners
     *   7 = Waitlist learners
     * 
     * Filter Types (for parents/learners):
     *   0 = All (no filter)
     *   1 = Families
     *   2 = Learners/Parents
     *   3 = Grades
     *   4 = Register classes
     *   5 = Parents/Learners
     *   6 = Tuition language
     *   8 = Sport team
     *   9 = Hostel boarders
     *   10 = Re-registration status
     *   11 = Promotion decision
     *   12 = Subject
     *   13 = Subject group
     *   14 = After-care
     * 
     * Filter Types (for staff):
     *   0 = All staff
     *   1 = Staff category
     *   2 = Specific staff members
     * 
     * @param {Object} filters - Frontend filter state from AdvancedFilteringModal
     * @param {string} [entityType] - Optional entity type for staff-specific handling
     * @returns {Object} filter_criteria object for API
     */
    buildFilterCriteria = (filters, entityType = null) => {
        // Ensure numeric values are integers as expected by d6+ API
        const status = parseInt(filters.status, 10) || 1
        const filterType = parseInt(filters.for, 10) || 0
        const gender = parseInt(filters.gender, 10) || 0
        const which = filters.which ? parseInt(filters.which, 10) : undefined
        
        // Ensure filter IDs are integers
        const filterIds = (filters.filter || []).map(id => 
            typeof id === 'string' ? parseInt(id, 10) : id
        ).filter(id => !isNaN(id))

        const criteria = {
            status: status,
            for: filterType,
            filter: filterIds,
            gender: gender,
        }

        // Always include current_on for date context (d6+ API expects this)
        // Format as YYYY-MM-DD string
        if (filters.currentOn) {
            criteria.current_on = moment(filters.currentOn).format('YYYY-MM-DD')
        } else {
            criteria.current_on = moment().format('YYYY-MM-DD')
        }

        // Add wait_year for waitlist learners (status 7)
        if (status === 7 && filters.waitYear) {
            criteria.wait_year = parseInt(filters.waitYear, 10) || new Date().getFullYear()
        }

        // Add which (parent type) for parents audience
        // 0 = Preferred parent, 1 = All parents
        if (which !== undefined && entityType !== 'staff') {
            criteria.which = which
        }

        return criteria
    }

    /**
     * Helper method to convert API filter_criteria back to frontend filter state
     * Used when loading a draft for editing
     * 
     * Converts integer values from API back to string format for frontend dropdowns
     * 
     * @param {Object} filterCriteria - filter_criteria object from API batch
     * @returns {Object} Frontend filter state for AdvancedFilteringModal
     */
    parseFilterCriteria = (filterCriteria) => {
        if (!filterCriteria) return null

        // Convert filter IDs to strings for frontend dropdown compatibility
        const filterIds = (filterCriteria.filter || []).map(id => String(id))

        return {
            // Convert integers back to strings for frontend dropdown compatibility
            status: String(filterCriteria.status ?? 1),
            currentOn: filterCriteria.current_on ? new Date(filterCriteria.current_on) : new Date(),
            waitYear: String(filterCriteria.wait_year ?? new Date().getFullYear()),
            for: String(filterCriteria.for ?? 0),
            filter: filterIds,
            which: String(filterCriteria.which ?? 1),
            gender: String(filterCriteria.gender ?? 0),
            search: '',
            page: 1,
            perPage: { text: '25', value: 25 },
        }
    }

    /**
     * Load all advanced filter batches for a notice and reconstruct filter states
     * Used when editing a draft notice
     * 
     * @param {Object} params
     * @param {string} params.communityId - Community ID
     * @param {string} params.noticeId - Notice UUID
     * @returns {Promise<Object>} Object with parents, learners, staff filter states and batch IDs
     */
    loadNoticeBatchesForEditing = async (params) => {
        const { communityId, noticeId } = params
        
        try {
            const response = await this.getNoticeBatches({ communityId, noticeId })
            const batches = response.data?.data || response.data || []
            
            const result = {
                parents: null,
                learners: null,
                staff: null,
            }
            
            for (const batch of batches) {
                const entityType = this._getEntityTypeFromApi(batch.entity_type)
                if (entityType && Object.prototype.hasOwnProperty.call(result, entityType)) {
                    result[entityType] = {
                        batchId: batch.id,
                        filters: this.parseFilterCriteria(batch.filter_criteria),
                        totalCount: batch.total_recipients,
                        status: batch.status,
                    }
                }
            }
            
            return result
        } catch (error) {
            console.error('Failed to load notice batches for editing:', error)
            return {
                parents: null,
                learners: null,
                staff: null,
            }
        }
    }

    /**
     * Convert API entity type back to frontend entity type
     * @private
     */
    _getEntityTypeFromApi = (apiEntityType) => {
        switch (apiEntityType) {
            case 'parent':
                return 'parents'
            case 'learner':
                return 'learners'
            case 'staff_member':
                return 'staff'
            default:
                return null
        }
    }
}

export default AdvancedFilteringApiService
